/**
 * PRÉPARER LA SÉANCE — chantier « manuel vivant », Φ-MV-5 (MV-TUT-04, F-11).
 * `/eleves/:id/manuel/preparation`.
 *
 * Une lecture : `GET /v1/tuteur/manuel/eleves/:eleveId/preparation-seance`. Le
 * serveur compose TOUT : ce qui s'est passé depuis la dernière séance, les
 * priorités (trois au plus, jamais de remplissage), leur titre, leur pourquoi,
 * leur appui, et le plan. L'écran ne choisit rien et n'écrit aucune de ces
 * phrases.
 *
 * Elle est atteinte de l'en-tête de la fiche (« Préparer la séance »), de
 * l'onglet « Séances », et de « Mes prochaines séances » dans la liste.
 *
 * ─── La charge (F-11, MV-UX-02) ─────────────────────────────────────────────
 *
 * En tête, compacts : avec qui, quand, et CE QUI ÉTAIT CONVENU la dernière
 * fois — c'est par là qu'une séance commence. Puis UNE section ouverte, « À
 * travailler, dans cet ordre ». Repliés, chacun avec son résumé : ce qui s'est
 * passé depuis la dernière séance, le plan, la prochaine évaluation.
 *
 * UN SEUL BOUTON PLEIN : « Déposer comme séquence ». Il n'écrit rien : il ouvre
 * la composition existante, préremplie de l'appui des priorités — le tuteur
 * relit, retire, complète, puis dépose. Sans appui à proposer, il n'est pas là.
 *
 * ─── Ce qui était convenu ───────────────────────────────────────────────────
 *
 * Le texte vient de la note de la dernière séance faite, que seul son auteur
 * lit. `lisible: false` : l'écran ne montre RIEN de la note, et le dit.
 *
 * ─── L'appui : des liens vers ce qui existe ─────────────────────────────────
 *
 * La fiche s'ouvre à son piège et la capsule à sa page, dans la relecture du
 * chapitre. Un exercice n'a pas de page à lui dans la console : il se lit ici,
 * par son énoncé, et se retrouve dans la banque de la composition.
 */
import {
  PHRASE_SANS_PRIORITE,
  denombrer,
  type EtatPreparation,
  type PreparationSeanceResponse,
  type PrioriteDeSeanceDto,
} from '@silex/shared';
import { useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge } from '../components/Badge';
import { CransNotion } from '../components/CransNotion';
import { Chargement, ErreurRessource } from '../components/EtatRessource';
import { SectionRabattable } from '../components/SectionRabattable';
import { TexteMaths } from '../components/TexteMaths';
import { manuelVivantActif } from '../lib/manuel-vivant';
import { adressesManuelEleve, dansCombien, jourCivil } from '../lib/manuel-tuteur-format';
import {
  brouillonDepuisPreparation,
  direJour,
  direJoursActifs,
  nomCourtDeNotion,
  resumeDepuis,
} from '../lib/manuel-tuteur-lectures';
import { adresses } from '../lib/savoir-format';
import type { BadgeTone } from '../lib/suivi-format';
import { formatDateHeure } from '../lib/tuteur-format';
import { useAsync } from '../lib/useAsync';

/** Jamais de rouge : « à renforcer » est en ardoise. */
const TON_PREPARATION: Readonly<Record<EtatPreparation, BadgeTone>> = {
  pret: 'acquis',
  en_bonne_voie: 'en-cours',
  a_renforcer: 'a-revoir',
  a_commencer: 'a-revoir',
};

const cle = (section: string): string => `manuel.preparation.${section}`;

export function PreparationSeancePage() {
  const { id } = useParams<{ id: string }>();
  const actif = manuelVivantActif();
  const resource = useAsync(
    useMemo(() => (actif && id ? () => api.manuelPreparationSeance(id) : null), [actif, id]),
  );

  if (!actif) return <Navigate to="/" replace />;
  if (!id) return <Navigate to="/eleves" replace />;

  return (
    <div className="content__inner manuel-tuteur">
      <p className="note-line fil-retour">
        <Link to={adressesManuelEleve.fiche(id, 'seances')}>← Ses séances</Link>
      </p>
      {resource.status === 'loading' ? <Chargement quoi="la préparation de la séance" /> : null}
      {resource.status === 'error' ? <ErreurRessource message={resource.message} /> : null}
      {resource.status === 'ready' ? <Preparation preparation={resource.data} /> : null}
    </div>
  );
}

/** La page, une fois lue : sans appel, elle s'éprouve par son rendu. */
export function Preparation({
  preparation: p,
}: {
  readonly preparation: PreparationSeanceResponse;
}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { eleve, chapitre } = p;
  const brouillon = useMemo(() => brouillonDepuisPreparation(p), [p]);
  /* L'administrateur lit, il ne dépose rien : le geste n'existe que pour un tuteur. */
  const peutDeposer = user?.role === 'tuteur' && brouillon.elements.length > 0;

  const quand =
    p.seance === null
      ? 'Aucune séance planifiée'
      : `${formatDateHeure(p.seance.debut)} · ${p.seance.dureeMin} min`;
  const avant =
    p.derniereSeanceLe === null
      ? 'première séance'
      : `dernière séance le ${direJour(p.derniereSeanceLe)}`;

  return (
    <>
      <div className="dash-topbar">
        <div>
          <p className="eyebrow">
            {chapitre.titre}
            {p.classe === null ? '' : ` · ${p.classe.libelle}`}
          </p>
          <h1 data-testid="preparation-titre">Séance avec {eleve.prenom}</h1>
          <p className="page-sub" data-testid="preparation-quand">
            {quand} · {avant}
          </p>
        </div>
        {peutDeposer ? (
          <div className="row wrap">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() =>
                navigate(adressesManuelEleve.composer(eleve.id), { state: { brouillon } })
              }
              data-testid="preparation-deposer"
            >
              Déposer comme séquence
            </button>
          </div>
        ) : null}
      </div>

      {/* ── Ce qui était convenu : par là qu'une séance commence ─────────── */}
      {p.convenu === null ? null : (
        <section className="card objectif-carte" data-testid="preparation-convenu">
          <h3>Convenu le {direJour(p.convenu.le)}</h3>
          {!p.convenu.lisible ? (
            <p className="note-line">
              La note de cette séance est réservée au tuteur qui l’a écrite.
            </p>
          ) : p.convenu.texte === null ? (
            <p className="note-line">Rien n’a été noté pour la suite.</p>
          ) : (
            <p className="seance-note">{p.convenu.texte}</p>
          )}
        </section>
      )}

      <div className="stack">
        {/* ── 1. À travailler — la seule section ouverte à l'arrivée ──────── */}
        <div data-testid="preparation-priorites">
          <SectionRabattable
            titre="À travailler, dans cet ordre"
            resume={
              p.priorites.length === 0
                ? 'Aucune priorité'
                : denombrer(p.priorites.length, 'priorité')
            }
            cleMemoire={cle('priorites')}
          >
            {p.priorites.length === 0 ? (
              <p className="note-line" data-testid="preparation-sans-priorite">
                {PHRASE_SANS_PRIORITE}
              </p>
            ) : (
              <ol className="priorites">
                {p.priorites.map((priorite) => (
                  <Priorite
                    key={priorite.rang}
                    priorite={priorite}
                    eleveId={eleve.id}
                    codeChapitre={chapitre.code}
                  />
                ))}
              </ol>
            )}
          </SectionRabattable>
        </div>

        {/* ── 2. Depuis la dernière séance — des comptes ──────────────────── */}
        <div data-testid="preparation-depuis">
          <SectionRabattable
            titre={p.depuis.titre}
            resume={resumeDepuis(p.depuis)}
            ouverteParDefaut={false}
            cleMemoire={cle('depuis')}
          >
            <div className="stat-tile-row">
              <div className="stat-tile">
                <span className="stat-tile__value">{p.depuis.travaux}</span>
                <span className="stat-tile__label">Travaux</span>
              </div>
              <div className="stat-tile">
                <span className="stat-tile__value">{p.depuis.reussis}</span>
                <span className="stat-tile__label">Réussis</span>
              </div>
              <div className="stat-tile">
                <span className="stat-tile__value">{p.depuis.minutes}</span>
                <span className="stat-tile__label">Minutes</span>
              </div>
              <div className="stat-tile">
                <span className="stat-tile__value">{p.depuis.aidesDemandees}</span>
                <span className="stat-tile__label">Indices demandés</span>
              </div>
            </div>

            {p.depuis.jours.length === 0 ? null : (
              <p className="jours-bande">
                <span className="jours-bande__cases" aria-hidden="true">
                  {p.depuis.jours.map((j) => (
                    <i key={j.date} className={j.actif ? 'on' : undefined} />
                  ))}
                </span>
                <span className="cell-sub">{direJoursActifs(p.depuis.jours)}</span>
              </p>
            )}

            {p.sequenceEnCours === null ? null : (
              <p className="note-line" data-testid="preparation-sequence">
                <strong>Séquence en cours.</strong> « {p.sequenceEnCours.titre} » —{' '}
                {p.sequenceEnCours.libelleEtat}
                {p.sequenceEnCours.pourLe === null
                  ? ''
                  : ` · à faire pour le ${jourCivil(p.sequenceEnCours.pourLe)}`}
                . <Link to={adressesManuelEleve.fiche(eleve.id, 'sequences')}>Détail</Link>
              </p>
            )}

            <h4>Erreurs de la période</h4>
            {p.depuis.erreurs.length === 0 ? (
              <p className="note-line">
                {p.depuis.travaux === 0
                  ? 'Aucun travail sur la période.'
                  : 'Aucune erreur nommée sur la période.'}
              </p>
            ) : (
              <div className="list list--programme">
                {p.depuis.erreurs.map((e) => (
                  <div key={e.code} className="li">
                    <span className="li-main">
                      <span className="li-title">{e.titre}</span>
                      <span className="li-sub">
                        Vue {denombrer(e.fois, 'fois', 'fois')}
                        {e.exemple === null ? null : (
                          <>
                            {' · '}
                            <TexteMaths texte={e.exemple} />
                          </>
                        )}
                      </span>
                    </span>
                    <Link
                      className="btn btn--ghost btn--sm"
                      to={adressesManuelEleve.travauxDeLErreur(eleve.id, e.code)}
                    >
                      Voir les copies
                    </Link>
                  </div>
                ))}
              </div>
            )}

            <p className="note-line" data-testid="preparation-dossier">
              {denombrer(p.travauxAuDossier, 'travail', 'travaux')} au dossier.{' '}
              <Link to={adressesManuelEleve.fiche(eleve.id, 'travaux')}>Lire les corrections</Link>
            </p>
          </SectionRabattable>
        </div>

        {/* ── 3. Le plan suggéré, replié ──────────────────────────────────── */}
        <div data-testid="preparation-plan">
          <SectionRabattable
            titre={`Plan suggéré — ${p.dureeMin} min`}
            resume={denombrer(p.plan.length, 'temps', 'temps')}
            ouverteParDefaut={false}
            cleMemoire={cle('plan')}
          >
            {p.plan.length === 0 ? (
              <p className="note-line">Aucun plan à proposer.</p>
            ) : (
              <ol className="plan">
                {p.plan.map((ligne, i) => (
                  <li key={i}>
                    <strong>{ligne.minutes} min</strong> — <TexteMaths texte={ligne.quoi} />
                  </li>
                ))}
              </ol>
            )}
          </SectionRabattable>
        </div>

        {/* ── 4. La prochaine évaluation : un état, des paliers ───────────── */}
        <div data-testid="preparation-evaluation">
          <SectionRabattable
            titre="Prochaine évaluation"
            resume={
              p.evaluation === null
                ? 'Aucune à venir'
                : `${p.evaluation.evaluation.titre}, ${dansCombien(p.evaluation.evaluation.joursRestants)} · ${p.evaluation.libelleEtat}`
            }
            ouverteParDefaut={false}
            cleMemoire={cle('evaluation')}
          >
            {p.evaluation === null ? (
              <p className="note-line">Aucune évaluation à venir dans l’objectif.</p>
            ) : (
              <div className="preparation">
                <div className="row-between wrap">
                  <div>
                    <strong>{p.evaluation.evaluation.titre}</strong>
                    <span className="cell-sub">
                      {p.evaluation.evaluation.libelleNature} ·{' '}
                      {jourCivil(p.evaluation.evaluation.date)} ·{' '}
                      {dansCombien(p.evaluation.evaluation.joursRestants)}
                    </span>
                  </div>
                  <Badge
                    tone={TON_PREPARATION[p.evaluation.etat]}
                    label={p.evaluation.libelleEtat}
                  />
                </div>
                <div className="list list--programme">
                  {p.evaluation.parNotion.map((n) => (
                    <div key={n.notion.code} className="li">
                      <span className="li-main">
                        <span className="li-title">{nomCourtDeNotion(n.notion)}</span>
                      </span>
                      {n.couverte ? (
                        <CransNotion palier={n.palier} libelle={n.libellePalier} />
                      ) : (
                        <span className="cell-sub">Non couverte</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionRabattable>
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────── une priorité, et son appui ─────────────────────────── */

function Priorite({
  priorite: x,
  eleveId,
  codeChapitre,
}: {
  readonly priorite: PrioriteDeSeanceDto;
  readonly eleveId: string;
  readonly codeChapitre: string;
}) {
  const { fiche, capsule, exercices } = x.appui;
  /* `UX-7-34` : les travaux où l'erreur a été vue ne sont pas un APPUI (une
     fiche, une capsule) : le lien suit le titre de l'erreur — qui dit déjà,
     de la main du serveur, combien de fois elle a été vue. */
  const sansLien = fiche === null && capsule === null;
  return (
    <li className="priorite" data-testid="preparation-priorite">
      <span className="sequence-element__rang" aria-hidden="true">
        {x.rang}
      </span>
      <div className="priorite__corps">
        <h4 className="priorite__titre">
          <TexteMaths texte={x.titre} />
          {x.erreur === null ? null : (
            <span className="priorite__travaux">
              {' — '}
              <Link
                to={adressesManuelEleve.travauxDeLErreur(eleveId, x.erreur.code)}
                data-testid="preparation-voir-travaux"
              >
                voir les travaux
              </Link>
            </span>
          )}
        </h4>
        <p className="priorite__pourquoi">
          <TexteMaths texte={x.pourquoi} />
        </p>

        {/* Les pas du plan d'une évaluation ne sont plus servis (AB#977). */}

        <dl className="lecture" data-testid="preparation-appui">
          {sansLien && exercices.length > 0 ? null : (
            <>
              <dt>Appui</dt>
              <dd>
                {sansLien ? (
                  'Aucune ressource précise à proposer.'
                ) : (
                  <span className="priorite__liens">
                    {fiche === null ? null : (
                      <Link to={adresses.fiche(codeChapitre, fiche.code, fiche.ancre ?? undefined)}>
                        {fiche.ancre === null ? 'Fiche' : 'Fiche, au piège'} : {fiche.titre}
                      </Link>
                    )}
                    {capsule === null ? null : (
                      <Link to={adresses.capsule(codeChapitre, capsule.code)}>
                        Capsule : {capsule.titre}
                      </Link>
                    )}
                  </span>
                )}
              </dd>
            </>
          )}
          {exercices.length === 0 ? null : (
            <>
              <dt>À faire ensemble</dt>
              <dd>
                {exercices.map((e) => (
                  <span key={e.code} className="priorite__exercice">
                    <TexteMaths texte={e.titre} />
                    <span className="cell-sub">
                      {e.nomNiveau} · {e.minutes} min · {e.pourquoi}
                    </span>
                  </span>
                ))}
              </dd>
            </>
          )}
        </dl>
      </div>
    </li>
  );
}
